import {
    AccountCore,
    ContactStatus,
    EncodingType,
    Form,
    FormPayload,
    FormSettingsEnum,
    FormSubtypeEnum,
    FormTypeEnum,
    TemplateScope,
} from '@dev/ee-api/ee-api-models';
import isEmail from 'validator/lib/isEmail';
import { find, isEmpty, includes, isArray, remove, isNull, isNil } from 'lodash-es';
import { Template, TemplateType } from '@dev/ee-api/ee-api-models';
import { all, call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { sendEventToGTM } from '@dev/ee-tools';
import { ERROR_TOAST_DURATION } from '@/services/constants';
import { getDialogStore, HideDialog, UpdateDialog } from '@/store/dialog/actions';
import { getMediaManagerStore, HideMediaManager } from '@/store/mediaManager/actions';
import { addList, loadSingleContact } from '@/services/ElasticApi';
import { formsPagePreview } from '@/services/ApiCategories/forms';
import {
    AddContactListErrorStatus,
    CloseGallery,
    LoadError,
    LoadPageTemplateSuccess,
    LoadSuccess,
    ModifyForm,
    SaveError,
    SaveStart,
    SaveSuccess,
    SetDefaultTemplate,
    SetFormErrors,
    SetIsTemplateOpen,
    SetTemplatePreview,
    TemplateLoadError,
    TemplateLoadStart,
    TemplateLoadSuccess,
    TemplateSaveError,
    TemplateSaveSuccess,
    UpdateContactLists,
    getFormCreatorStore,
} from '@/store/formCreator/actions';
import {
    AddContactListStartAction,
    FormCreatorActionTypes,
    FormCreatorState,
    FormErrorNames,
    LoadPageTemplateStartAction,
    LoadStartAction,
    TemplateLoadStartAction,
    TemplateOpenFromGalleryAction,
    TemplatePreviewFromGalleryAction,
    SaveStartAction,
    FormPopupTriggerEnum,
    FormMetaData,
    TemplateSaveStartAction,
} from '@/store/formCreator/types';
import { loadForm, saveForm } from '@/services/ApiCategories/forms';
import { encodeFormMetaData } from '@/components/FormCreator/tools/encodeFormMetaData';
import { EditorMetaData } from '@/store/templateEditor/types';
import { ListsGetNamesStart } from '@/store/lists/actions';
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { getRecaptchaScript } from '@/components/FormCreator/tools/getRecaptchaScript';
import { Logger } from '@/services/errors/appLogger';
import {
    saveTemplate as createTemplate,
    loadTemplate as fetchTemplate,
    getDefaultTemplateObj,
    previewTemplate,
    updateTemplate,
} from '../services/ApiCategories/templates';
import { HideToast, ShowToast } from '../store/toast/actions';

const getPopupTriggerHtml = (formMetaData: FormMetaData): string => {
    if (!formMetaData) {
        return '';
    }
    const { data } = formMetaData;
    const { popupTrigger, popupDelay } = data;
    return `<div class="popup-trigger" style="display:none;" data-trigger="${popupTrigger}" data-value="${popupDelay}"></div>`; //don't use self closing tag
};

const getFormHtml = ({
    html,
    isPopup,
    metaData,
    popupTrigger,
    popupDelay,
}: {
    html: string;
    isPopup: boolean;
    metaData?: FormMetaData;
    popupTrigger?: FormPopupTriggerEnum;
    popupDelay?: number;
}): string => {
    if (!html) {
        return '';
    }
    if (!isPopup) {
        return html;
    }
    const { data } = metaData || {};
    const { popupTrigger: metaPopupTrigger, popupDelay: metaPopupDelay } = data || {};
    let htmlWithPopupSettings = html;
    if (metaPopupTrigger && !isNil(metaPopupDelay)) {
        const oldSettings = getPopupTriggerHtml(metaData);
        htmlWithPopupSettings = html?.replace(oldSettings, '');
    }
    if (popupTrigger && !isNil(popupDelay)) {
        const newSettings = getPopupTriggerHtml({ data: { popupTrigger, popupDelay } });
        htmlWithPopupSettings = htmlWithPopupSettings?.replace('</body>', `${newSettings}</body>`);
    }
    return htmlWithPopupSettings;
};

const ShowTemplateErrorToast = (message: string) =>
    ShowToast({
        severity: 'error',
        message,
        duration: ERROR_TOAST_DURATION,
        width: 485,
        onClose: HideToast,
    });

function* onClose(): any {
    const { isOpened } = yield select(getDialogStore);
    const { isOpened: isMediaManagerOpened } = yield select(getMediaManagerStore);
    if (isOpened) {
        yield put(HideDialog());
    }
    if (isMediaManagerOpened) {
        yield put(HideMediaManager());
    }
}

function* showSaveErrors(exitOnSave?: boolean): any {
    const { template }: FormCreatorState = yield select(getFormCreatorStore);
    if (!template.saveErrors || !exitOnSave) {
        return;
    }
    yield all(template.saveErrors.map((err) => put(ShowTemplateErrorToast(err.message))));
}

function* load(action: LoadStartAction): any {
    const { payload } = action;
    const { name } = payload;

    try {
        const formObject: Form = yield call(loadForm, name);
        if (formObject?.FormTemplateName) {
            yield put(TemplateLoadStart(formObject?.FormTemplateName, false, false));
            yield take(FormCreatorActionTypes.TEMPLATE_LOAD_SUCCESS);
        }
        yield put(LoadSuccess(formObject));
    } catch (e) {
        yield put(LoadError());
        yield call(errorToast, e);
    }
}

function* getContactStatus(email: string) {
    try {
        const contact = yield call(loadSingleContact, email);
        return contact.Status;
    } catch (e) {
        return 'Error';
    }
}

function* verifyForm(EmailsToNotifyOnAction: string, formSettings: FormSettingsEnum[]) {
    const errors: Partial<Record<FormErrorNames, boolean>> = {};
    const emailsToNotify = EmailsToNotifyOnAction?.split(';').map((item) => item.trim());
    const emailsNumberFromString = (EmailsToNotifyOnAction?.split('@').length ?? 0) - 1;

    if (!isEmpty(emailsToNotify) && includes(formSettings, FormSettingsEnum.NotifyEmails)) {
        const emailsInvalid = !!find(emailsToNotify, (email) => !isEmail(email));
        if (emailsInvalid) {
            errors[FormErrorNames.NOTIFY_INVALID_EMAILS] = true;
        }
        if (emailsNumberFromString > 1 && emailsNumberFromString !== emailsToNotify.length) {
            errors[FormErrorNames.NOTIFY_EMAILS_NO_PROPER_SEPARATOR] = true;
        }
        if (!emailsInvalid && emailsToNotify.length > 5) {
            errors[FormErrorNames.NOTIFY_EMAILS_NEMBER_EXCEEDED] = true;
        }
        const recipientsStatuses = yield all(emailsToNotify.map((email) => call(getContactStatus, email)));
        const emailsStatusInvalid = !!find(
            recipientsStatuses,
            (status) => status !== ContactStatus.Active && status !== ContactStatus.Engaged
        );
        if (!emailsInvalid && !!emailsStatusInvalid) {
            errors[FormErrorNames.NOTIFY_EMAILS_NOT_ON_CONTACT_LIST] = true;
        }
    }

    return { errors };
}

const getFormMetaDataToSave = ({
    isPopup,
    popupDelay,
    popupTrigger,
}: {
    isPopup: boolean;
    popupDelay?: number;
    popupTrigger?: FormPopupTriggerEnum;
}): string | null => {
    if (!isPopup || isNil(popupDelay) || isNil(popupTrigger)) {
        return null;
    }
    return encodeFormMetaData({
        data: {
            popupTrigger,
            popupDelay,
        },
    });
};

function* save(action: SaveStartAction): any {
    const { formObject, formPayload, contactLists, popupDelay, popupTrigger, metaData, saveSettings } = yield select(
        getFormCreatorStore
    );

    const {
        Name,
        Html,
        Css,
        FormType,
        FormTemplateName,
        Audience,
        MergeFields,
        FormScope: Scope,
        FormSettings,
        ActivationTemplateName,
        DoubleOptInPage,
        FormAlreadySubscribedPage,
        FormPageType,
        FormSubmitPage,
    } = (formObject as Form) ?? {};

    const emailsToNotify = (formPayload?.EmailsToNotifyOnAction ?? formObject?.EmailsToNotifyOnAction)
        ?.split(';')
        .map((item) => item.trim())
        .join(';');

    const isPopup = action.payload.isPopupCreator || formObject?.FormSubtype === FormSubtypeEnum.Popup;

    const payload: FormPayload = {
        Name,
        Html: getFormHtml({ html: Html, isPopup, metaData, popupTrigger, popupDelay }),
        Css,
        Scope,
        FormType: FormType ?? FormTypeEnum.FormCreator,
        FormTemplateName,
        Audience: contactLists ?? Audience?.map((item) => item.PublicListID),
        MergeFields,
        FormSettings,
        ActivationTemplateName,
        DoubleOptInPage,
        FormAlreadySubscribedPage,
        FormPageType,
        FormSubmitPage,
        ...formPayload,
        EmailsToNotifyOnAction: emailsToNotify,
        FormSubtype: isPopup ? FormSubtypeEnum.Popup : FormSubtypeEnum.Inline,
        MetaData: getFormMetaDataToSave({ isPopup, popupDelay, popupTrigger }),
    };

    try {
        const { errors } = yield call(verifyForm, payload?.EmailsToNotifyOnAction, payload?.FormSettings);
        if (!saveSettings?.isPublic && !isEmpty(errors)) {
            yield put(SetFormErrors(errors));
            return;
        }
        const formObjectResponse: Template = yield call(saveForm, payload, formObject?.Name);
        yield put(SaveSuccess(formObjectResponse));

        const isFirstTotalFormSave =
            isNull(formObject?.EmailsToNotifyOnAction) && !isNil(formPayload?.EmailsToNotifyOnAction);
        if (isFirstTotalFormSave) {
            const generalInfo = yield select((state): AccountCore => state?.account?.GeneralInfo);
            sendEventToGTM('form generated', {
                productType: generalInfo?.ProductType,
            });
        }
    } catch (e) {
        if (e?.response?.data?.MessageID === 'form_name_in_use') {
            yield put(SetFormErrors({ [FormErrorNames.DUPLICATED_NAME]: true }));
            return;
        }
        Logger.error(e?.response?.data?.Error ?? e);
        yield put(SaveError());
        yield call(errorToast, e);
    }
}

function* loadTemplate(action: TemplateLoadStartAction): any {
    const { payload } = action;
    const { name, isPublic, useDraftScope } = payload;

    try {
        if (!name) {
            const getTemplate = yield call(getDefaultTemplateObj, TemplateType.FormTemplate);
            yield put(SetDefaultTemplate({ templateObject: getTemplate?.data }));
        } else {
            const template: Template = yield call(fetchTemplate, name, isPublic, TemplateType.FormTemplate);
            yield put(TemplateLoadSuccess(template, isPublic, useDraftScope));
        }
    } catch (e) {
        yield put(TemplateLoadError());
        yield call(errorToast, e);
    }
}

const prepareFormHtml = (content: string, formObject: Form, metaData: FormMetaData, isRecaptchaOn: boolean): string =>
    content?.replace(
        '</body>',
        `${isRecaptchaOn ? getRecaptchaScript() : ''}${
            formObject?.FormSubtype === FormSubtypeEnum.Popup ? getPopupTriggerHtml(metaData) : ''
        }</body>`
    );

function* saveTemplate({ payload, meta }: TemplateSaveStartAction): any {
    const { data, exitOnSave, errors } = payload;

    const { template, formObject, formPayload, metaData, saveSettings }: FormCreatorState = yield select(
        getFormCreatorStore
    );
    try {
        if (!saveSettings?.isGalleryCreator && errors) {
            throw errors;
        }
        const metaDataPayload: EditorMetaData = {
            data: { contentJson: data?.contentJson, recentColors: template.recentColors ?? [] },
        };
        const payload: Template = {
            TemplateType: TemplateType.FormTemplate,
            Name: saveSettings?.isPublic ? formObject.Name : template.name,
            Base64Image: saveSettings?.customScreenshot ?? data.screenshot,
            EditorMetaData: Buffer.from(JSON.stringify(metaDataPayload)).toString('base64'),
            TemplateScope: saveSettings?.isPublic ? TemplateScope.Public : TemplateScope.Draft,
            IsPublic: false,
            IsDefault: false,
            Body: {
                Parts: saveSettings?.isPublic
                    ? [
                          {
                              Content: data.content,
                              ContentType: 'text/html',
                              Charset: null,
                              Encoding: EncodingType.None,
                          },
                      ]
                    : [],
            },
        };
        const response = yield template.isNew
            ? call(createTemplate, payload)
            : call(updateTemplate, formObject.FormTemplateName, payload);
        yield put(TemplateSaveSuccess(data));

        const templateName = payload.Name;
        const formSettings = formPayload?.FormSettings ?? formObject?.FormSettings ?? [];
        const isRecaptchaInSettings = includes(formSettings, FormSettingsEnum.RequireReCaptcha);
        const isRecaptchaOn = !!data.features?.recaptchaRequiredBlockIds?.length;
        const newFormSettings =
            isRecaptchaOn && !isRecaptchaInSettings
                ? [...formSettings, FormSettingsEnum.RequireReCaptcha]
                : remove([...formSettings], (setting) => setting !== FormSettingsEnum.RequireReCaptcha);

        yield put(
            ModifyForm({
                FormTemplateName: templateName,
                Screenshot: response?.Screenshot,
                MediumScreenshot: response?.MediumScreenshot,
                Thumbnail: response?.Thumbnail,
                Html: prepareFormHtml(data.content, formObject, metaData, isRecaptchaOn),
                FormSettings: newFormSettings,
            })
        );
        yield put(SaveStart());

        if (exitOnSave) {
            yield put(SetIsTemplateOpen(false, meta));
        }
    } catch (e) {
        if (isArray(e)) {
            yield call(showSaveErrors, exitOnSave);
            yield put(TemplateSaveError());
            return;
        }
        yield call(errorToast, e);
        yield put(TemplateSaveError());
    }
}

function* createContactList({ payload }: AddContactListStartAction): any {
    try {
        yield put(UpdateDialog({ isLoading: true }));
        const addedListResponse = yield call(addList, payload.list);
        const listId = addedListResponse?.data?.PublicListID;
        const { contactLists = [] } = yield select(getFormCreatorStore);
        yield put(UpdateContactLists([...contactLists, listId]));
        yield put(ListsGetNamesStart());
        yield put(HideDialog());
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e);
        yield put(UpdateDialog({ isLoading: false }));
        yield put(AddContactListErrorStatus(true));
    }
}

function* loadPageTemplateSaga({ payload }: LoadPageTemplateStartAction): any {
    const { pageTemplateType } = payload;
    try {
        const pageTemplate = yield call(formsPagePreview, pageTemplateType);
        yield put(LoadPageTemplateSuccess(pageTemplateType, pageTemplate?.data));
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e);
        yield call(errorToast, e);
    }
}
function* openTemplateFromGallery(action: TemplateOpenFromGalleryAction): any {
    const { templateName, isPublic } = action.payload;
    try {
        yield put(TemplateLoadStart(templateName, isPublic, true));
        yield put(CloseGallery());
        yield put(SetIsTemplateOpen(true));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* previewTemplateFromGallery(action: TemplatePreviewFromGalleryAction): any {
    const { payload } = action;
    try {
        const templateContent = yield call(
            previewTemplate,
            payload.templateName,
            payload.isPublic,
            TemplateType.FormTemplate
        );
        if (!templateContent) {
            return;
        }
        yield put(SetTemplatePreview(templateContent, payload.templateName, payload.isPublic));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* watchLoadStart(): any {
    yield takeEvery(FormCreatorActionTypes.LOAD_START, load);
    yield takeEvery(FormCreatorActionTypes.SAVE_START, save);
    yield takeEvery(FormCreatorActionTypes.TEMPLATE_LOAD_START, loadTemplate);
    yield takeEvery(FormCreatorActionTypes.TEMPLATE_SAVE_START, saveTemplate);
    yield takeLatest(FormCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS, showSaveErrors);
    yield takeLatest(FormCreatorActionTypes.CLOSE, onClose);
    yield takeLatest(FormCreatorActionTypes.ADD_CONTACT_LIST_START, createContactList);
    yield takeEvery(FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_START, loadPageTemplateSaga);
    yield takeLatest(FormCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY, openTemplateFromGallery);
    yield takeLatest(FormCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY, previewTemplateFromGallery);
}

const formCreatorSagas = [fork(watchLoadStart)];

export default formCreatorSagas;
